/**
 * Playback of the reference tones.
 *
 * The synthesis itself is a pure function ({@link renderPluck}); this module
 * is only the Web Audio side of it — an output context, a cache of rendered
 * buffers, and exactly one voice sounding at a time.
 *
 * Deliberately its own AudioContext rather than the capture engine's: a
 * reference tone has to work before the microphone has ever been opened, and
 * tying playback to a running tuner would make the button dead until then.
 */

import { AudioEngineError } from './errors'
import {
  DEFAULT_DECAY_SECONDS,
  DEFAULT_DURATION_SECONDS,
  renderPluck,
  type PluckOptions,
} from './karplus-strong'

/** What the UI needs from a reference-tone player. */
export interface ReferenceTonePort {
  /**
   * Start a tone, replacing whatever was sounding.
   *
   * @returns How long the new tone will sound, in seconds.
   */
  play(frequency: number): number
  /** Fade the current tone out. */
  stop(): void
  /** Release the audio context. */
  close(): Promise<void>
}

/** Player configuration. */
export interface ReferenceToneOptions {
  /** How the output context is created; injected in tests. */
  readonly createContext?: () => AudioContext
  /** Time for a tone to fall by 60 dB, in seconds. */
  readonly decaySeconds?: number
  /** Rendered length of a tone, in seconds. */
  readonly durationSeconds?: number
  /** Fade applied when a tone is replaced or stopped, in seconds. */
  readonly fadeOutSeconds?: number
  /** How many rendered buffers to keep. */
  readonly cacheSize?: number
}

/**
 * Fade applied when a tone is cut short.
 *
 * Long enough that stopping mid-ring is inaudible as a click, short enough
 * that the old string does not blur into the new one.
 */
export const DEFAULT_FADE_OUT_SECONDS = 0.08

/**
 * Rendered buffers kept in memory.
 *
 * Six strings per tuning, and the concert pitch slider can move every one of
 * them; a handful of tunings' worth is plenty, and each entry is only a couple
 * of hundred kilobytes.
 */
export const DEFAULT_CACHE_SIZE = 24

/** Web Audio constructor, including the prefixed one older Safari exposes. */
interface WebAudioGlobal {
  AudioContext?: new () => AudioContext
  webkitAudioContext?: new () => AudioContext
}

/** Create an output context, or say clearly that this browser has none. */
export function createOutputContext(): AudioContext {
  const scope = globalThis as WebAudioGlobal
  const Constructor = scope.AudioContext ?? scope.webkitAudioContext
  if (!Constructor) {
    throw new AudioEngineError('unsupported', 'Dieser Browser bietet die Web Audio API nicht an.')
  }
  return new Constructor()
}

/** One sounding tone and the nodes that have to be torn down with it. */
interface Voice {
  readonly source: AudioBufferSourceNode
  readonly gain: GainNode
}

/** Plays plucked reference tones, one at a time. */
export class ReferenceTonePlayer implements ReferenceTonePort {
  private context: AudioContext | null = null
  private voice: Voice | null = null
  private readonly buffers = new Map<string, AudioBuffer>()

  constructor(private readonly options: ReferenceToneOptions = {}) {}

  /** True while a tone is sounding. */
  get isPlaying(): boolean {
    return this.voice !== null
  }

  /** How many rendered buffers are currently cached. */
  get cachedCount(): number {
    return this.buffers.size
  }

  /**
   * Start a tone at the given frequency.
   *
   * Safe to call straight from a click handler: the context is constructed
   * here, inside the gesture, which is what Safari and iOS require.
   *
   * @returns Length of the tone in seconds.
   */
  play(frequency: number): number {
    const context = (this.context ??= (this.options.createContext ?? createOutputContext)())

    // A context can start suspended when the page has never had a gesture, and
    // it is suspended again after a tab switch.
    if (context.state === 'suspended') {
      void context.resume()
    }

    this.fadeOut(context)

    const buffer = this.bufferFor(context, frequency)
    const source = context.createBufferSource()
    source.buffer = buffer

    // The buffer already fades in and out on its own; this gain exists only so
    // a tone can be cut short without a click.
    const gain = context.createGain()
    gain.gain.setValueAtTime(1, context.currentTime)

    source.connect(gain)
    gain.connect(context.destination)

    const voice: Voice = { source, gain }
    source.onended = () => {
      source.disconnect()
      gain.disconnect()
      if (this.voice === voice) this.voice = null
    }

    this.voice = voice
    source.start()
    return buffer.duration
  }

  /** Fade the current tone out, if there is one. */
  stop(): void {
    if (this.context) this.fadeOut(this.context)
  }

  /** Stop and release the audio context. */
  async close(): Promise<void> {
    this.stop()
    this.buffers.clear()
    const { context } = this
    this.context = null
    this.voice = null
    if (!context) return
    try {
      await context.close()
    } catch (error) {
      console.error('[multitimbral] reference tone context could not be closed', error)
    }
  }

  /** Ramp the sounding voice down and schedule it to stop. */
  private fadeOut(context: AudioContext): void {
    const voice = this.voice
    if (!voice) return
    this.voice = null

    const fade = this.options.fadeOutSeconds ?? DEFAULT_FADE_OUT_SECONDS
    const now = context.currentTime
    voice.gain.gain.setValueAtTime(voice.gain.gain.value, now)
    voice.gain.gain.linearRampToValueAtTime(0, now + fade)
    voice.source.stop(now + fade)
    // Disconnecting immediately would cut the ramp off; do it once it is done.
    voice.source.onended = () => {
      voice.source.disconnect()
      voice.gain.disconnect()
    }
  }

  /**
   * Rendered buffer for a frequency, synthesised on first use.
   *
   * Keyed by sample rate as well: the same note is a different set of samples
   * at 44.1 and 48 kHz, and the context decides which.
   */
  private bufferFor(context: AudioContext, frequency: number): AudioBuffer {
    const { sampleRate } = context
    const key = `${String(sampleRate)}|${frequency.toFixed(4)}`

    const cached = this.buffers.get(key)
    if (cached) {
      // Re-insert so the map's iteration order stays least-recently-used first.
      this.buffers.delete(key)
      this.buffers.set(key, cached)
      return cached
    }

    const pluck: PluckOptions = {
      frequency,
      sampleRate,
      decaySeconds: this.options.decaySeconds ?? DEFAULT_DECAY_SECONDS,
      durationSeconds: this.options.durationSeconds ?? DEFAULT_DURATION_SECONDS,
    }
    const samples = renderPluck(pluck)
    const buffer = context.createBuffer(1, samples.length, sampleRate)
    buffer.copyToChannel(samples, 0)

    const limit = this.options.cacheSize ?? DEFAULT_CACHE_SIZE
    const oldest = this.buffers.keys().next()
    if (this.buffers.size >= limit && !oldest.done) {
      this.buffers.delete(oldest.value)
    }
    this.buffers.set(key, buffer)
    return buffer
  }
}
